import React, { useEffect, useState } from 'react';
import Button from '../../Form/Button';
import { TbEraser, TbX } from 'react-icons/tb';
import Badge from '../Badge';
import Drawer from '../Drawer';
import ButtonPrimary from '../../Form/ButtonPrimary';
import Confirm from '../Confirm';
import Modal from '../Modal';
import ProductsFilterChips from './ProductsFilterChips';
import { useLayoutContext } from '../../../Contexts/LayoutContext';
import { useTranslation } from 'react-i18next';
import { PiSlidersHorizontalBold } from "react-icons/pi";
import { filterCategoryByTags, getCategoryProductCount, hasProductWithTag, isArraysEqual } from '../../../Utils/Helpers';
import BadgeIndicator from './BadgeIndicator';

interface ProductsFilterProps {
    category: any;
    className?: string;
}

type StoredFilterGroup = {
    category_id: number;
    filters: Filter[];
};

type Filter = {
    id: number;
    name: string;
    slug: string;
    icon: string | null;
    group_slug?: string;
};

const STORAGE_KEY = 'product_filters';

const ProductsFilter = ({
    category,
    className
}: ProductsFilterProps) => {

    const { t } = useTranslation();
    const { isMobile, allCategories, categories, setCategories, loadingCategories } = useLayoutContext();
    const [ showConfirmReset, setShowConfirmReset ] = useState(false);
    const [ showFilter, setShowFilter ] = useState(false);
    const [ filters, setFilters ] = useState<any[]>([]);
    const [ selectedFilters, setSelectedFilters ] = useState<Filter[]>([]);

    const getStoredGroups = (): StoredFilterGroup[] => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];

        try {
            return JSON.parse(stored) as StoredFilterGroup[];
        } catch {
            return [];
        }
    };

    useEffect(() => {
        if (category.tag_categories) {
            const enrichedFilters = category.tag_categories.map((group: any) => ({
                ...group,
                tags: group.tags.map((tag: any) => ({
                    ...tag,
                    group_slug: group.slug
                }))
            }));
            setFilters(enrichedFilters);
        }
        return () => {
            setFilters([]);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    useEffect(() => {
        const groups = getStoredGroups();
        const group = groups.find(g => g.category_id === category.id);

        setSelectedFilters(group ? group.filters : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUpdateCategories = () => {
        if (loadingCategories) return;

        const groups = getStoredGroups();

        const updated = allCategories.map((cat: any) => {
            const group = groups.find(g => g.category_id === cat.id);
            if (group && group.filters.length > 0) {
                return filterCategoryByTags(cat, group.filters);
            }
            return cat;
        });

        if (!isArraysEqual(updated, categories)) {
            //* Removendo produtos em destaque (desktop - é exibido no header)
            const updatedWithoutFeaturedProducts = updated.filter((cat: any) => cat.id != null);
            setCategories(updatedWithoutFeaturedProducts);
        }
    };

    useEffect(() => {
        handleUpdateCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters, loadingCategories]);


    const handleAddFilter = (filter: Filter) => {
        setSelectedFilters(prev => {
            if (prev.includes(filter)) return prev;

            const updated = [...prev, filter];
            const groups = getStoredGroups();

            const index = groups.findIndex(g => g.category_id === category.id);

            if (index >= 0) {
                groups[index].filters = updated;
            } else {
                groups.push({ category_id: category.id, filters: updated });
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
            return updated;
        });
    };

    const handleRemoveFilter = (filterId: number) => {
        setSelectedFilters(prev => {
            const updated = prev.filter(filter => filter.id !== filterId);

            const groups = getStoredGroups();
            const index = groups.findIndex(g => g.category_id === category.id);

            if (index >= 0) {
                groups[index].filters = updated;

                if (updated.length === 0) {
                    groups.splice(index, 1);
                }
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
            return updated;
        });
    };


    // Resetar apenas o grupo atual
    const handleResetFilters = () => {
        setSelectedFilters([]);

        const groups = getStoredGroups().filter(g => g.category_id !== category.id);

        localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
        setShowConfirmReset(false);
    };

    // Verifica se filtro está selecionado
    const isFilterSelected = (filterId: number) => {
        const groups = getStoredGroups();
        const group = groups.find(g => g.category_id === category.id);

        return group ? group.filters.some(filter => filter.id === filterId) : false;
    };

    if (!category.has_filter || !filters) return null;

    return (
        <>
            <div className={`flex flex-col lg:flex-row gap-4 ${className}`}>
                <Button onClick={() => setShowFilter(true)} className="w-full md:w-max gap-1">
                    <PiSlidersHorizontalBold />
                    <span>{selectedFilters.length > 0 ? t('products.filters.button_label_filtered') : t('products.filters.drawer_title', { categoryName: category.name })}</span>
                    {selectedFilters.length > 0 &&
                        <Badge type="custom" className="rounded-full justify-center text-xs w-6 h-6 ml-1">
                            {selectedFilters.length}
                        </Badge>
                    }
                </Button>

                <ProductsFilterChips selectedFilters={selectedFilters} onRemoveFilter={handleRemoveFilter} onResetFilters={handleResetFilters} />
            </div>

            <Drawer
                open={showFilter}
                fromBottom={isMobile}
                closeable={false}
                onClose={() => setShowFilter(false)}
                title={t('products.filters.drawer_title', { categoryName: category.name })}
            >
                <div className="flex flex-col gap-4 justify-between pt-3 pb-0 p-5 h-full">

                    <div className="flex flex-wrap gap-6 w-full pl-2">
                        {filters
                            .map(group => (
                            <div key={group.id} className="flex flex-wrap items-center gap-2 w-full">

                                {/* Nome do grupo de tags */}
                                <span className="w-full font-semibold text-shopfood-title border-b border-gray-200/75 border-dashed pb-1 mb-1 -ms-2">
                                    {group.name}
                                </span>

                                {/* Tags do grupo */}
                                {group.tags.map((tag:any) => (
                                    isFilterSelected(tag.id) ? (
                                        <ButtonPrimary
                                            outline
                                            key={tag.id}
                                            onClick={() => handleRemoveFilter(tag.id)}
                                            className="rounded-full h-auto text-left ring-inset fadeInIcon"
                                        >
                                            {tag.name}
                                        </ButtonPrimary>
                                    ) : (
                                        <Button
                                            key={tag.id}
                                            onClick={() => handleAddFilter(tag)}
                                            className="gap-0.5 rounded-full h-auto text-left ring-inset disabled:opacity-25 disabled:bg-gray-200/75"
                                            disabled={!hasProductWithTag(category, tag, selectedFilters)}
                                        >
                                            {tag.name}
                                        </Button>
                                    )
                                ))}
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-2 sticky bottom-0 py-4 bg-white/75 backdrop-blur-sm">
                        <Button
                            rounded
                            disabled={selectedFilters.length === 0}
                            onClick={() => setShowConfirmReset(true)}
                            className='gap-0.5 relative'
                            >
                            <span><TbEraser size={20} /></span>
                            {selectedFilters.length > 0 && <BadgeIndicator quantity={selectedFilters.length} className='absolute -top-2 -right-1 zoomInIconNoDelay' />}
                        </Button>

                        <ButtonPrimary
                            onClick={() => setShowFilter(false)}
                            className={`w-full gap-1`}
                            disabled={getCategoryProductCount(category) === 0}
                        >
                            <span>{t('products.filters.button_show_results_label')}</span>

                            <Badge
                                type="white"
                                outline
                                className="rounded-full justify-center text-xs h-6 min-w-6 ml-1 ring-2 zoomInIconNoDelay"
                            >
                                {getCategoryProductCount(category)}
                            </Badge>
                        </ButtonPrimary>

                        <Button rounded onClick={() => setShowFilter(false)}>
                            <span><TbX size={20} /></span>
                        </Button>
                    </div>
                </div>
            </Drawer>

            <Modal
                open={showConfirmReset}
                className="max-w-sm m-4"
                onClose={() => setShowConfirmReset(false)}
            >
                <Confirm
                    title={t('products.filters.confirm_reset.title')}
                    message={t('products.filters.confirm_reset.message')}
                    labelConfirm={t('products.filters.confirm_reset.label_confirm')}
                    labelCancel={t('products.filters.confirm_reset.label_cancel')}
                    onConfirm={handleResetFilters}
                    onCancel={() => setShowConfirmReset(false)}
                />
            </Modal>
        </>
    );
};

export default ProductsFilter;
